import { Loader2, Pause, Play, RotateCcw, RotateCw } from "lucide-react";
import type { JSX, KeyboardEvent, PointerEvent } from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  recordingPeaks,
  SKIP_SECONDS,
  useAudioEngine,
} from "@/lib/review/audio-engine";
import { formatMoment } from "@/lib/review/noteSources";
import { cn } from "@/lib/utils";
import { getVisitScope } from "@/lib/visit-queries";

// The v3.2 transcript player (redesign-v3.2 .c-player / .dr-player): docked
// at the bottom of the transcript drawer. Play/pause, ±10 s, a scrubber
// with the recording's mini waveform (a plain bar when it cannot be
// decoded), elapsed / remaining and speed. Space plays or pauses and the
// arrow keys skip while the player has focus.

export interface PlayerRecording {
  // The recording's audio route.
  path: string;
  label: string;
}

const Waveform = ({
  peaks,
  progress,
}: {
  peaks: number[] | null;
  progress: number;
}): JSX.Element =>
  peaks ? (
    <span
      aria-hidden
      className="flex h-6 w-full items-center gap-px overflow-hidden"
    >
      {peaks.map((peak, index) => (
        <i
          // biome-ignore lint/suspicious/noArrayIndexKey: fixed bars
          key={index}
          className={cn(
            "min-w-0 flex-1 rounded-full",
            index / peaks.length < progress ? "bg-v3-brand" : "bg-v3-text-3/45",
          )}
          style={{ height: `${Math.round(peak * 100)}%` }}
        />
      ))}
    </span>
  ) : (
    <span
      aria-hidden
      className="relative block h-1.5 w-full overflow-hidden rounded-full bg-v3-surface-3"
    >
      <span
        className="absolute inset-y-0 left-0 rounded-full bg-v3-brand"
        style={{ width: `${progress * 100}%` }}
      />
    </span>
  );

export const TranscriptPlayer = ({
  recordings,
  selected,
  onSelect,
  className,
}: {
  recordings: PlayerRecording[];
  selected: number;
  onSelect: (index: number) => void;
  className?: string;
}): JSX.Element | null => {
  const { t } = useTranslation();
  const engine = useAudioEngine();
  const recording = recordings[selected];
  const [peaks, setPeaks] = useState<number[] | null>(null);
  const bar = useRef<HTMLDivElement>(null);
  const path = recording?.path ?? null;

  // Loads the recording (and its waveform) when the drawer shows it, unless
  // another recording of the visit is already playing.
  useEffect(() => {
    if (!path) return;
    const state = useAudioEngine.getState();
    if (state.path !== path && !state.playing) void state.load(path);
    const scope = getVisitScope();
    let alive = true;
    setPeaks(null);
    if (scope)
      void recordingPeaks(path, scope.uid).then((value) => {
        if (alive) setPeaks(value);
      });
    return () => {
      alive = false;
    };
  }, [path]);

  if (!recording) return null;
  const current = engine.path === recording.path;
  const duration = current ? engine.duration : 0;
  const time = current ? engine.currentTime : 0;
  const progress = duration ? Math.min(1, time / duration) : 0;
  const playing = current && engine.playing;
  const loading = current && engine.status === "loading";

  const ensure = async (): Promise<boolean> =>
    useAudioEngine.getState().load(recording.path);
  const toggle = async (): Promise<void> => {
    if (!current && !(await ensure())) return;
    await useAudioEngine.getState().toggle();
  };
  const seekAt = (clientX: number): void => {
    const rect = bar.current?.getBoundingClientRect();
    if (!rect || !duration) return;
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    useAudioEngine.getState().seek(ratio * duration);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.target instanceof HTMLButtonElement && event.key === " ") return;
    if (event.key === " ") {
      event.preventDefault();
      void toggle();
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      useAudioEngine.getState().skip(5);
    } else if (event.key === "ArrowLeft") {
      event.preventDefault();
      useAudioEngine.getState().skip(-5);
    }
  };
  const iconButton =
    "inline-flex size-9 shrink-0 items-center justify-center rounded-full text-v3-text-2 transition-colors hover:bg-v3-surface-2 hover:text-v3-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v3-brand-ring disabled:opacity-40";

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: keyboard shortcuts for the focused player (its buttons and slider are focusable).
    <div
      className={cn("border-t border-v3-border pt-2.5 font-v3", className)}
      data-testid="transcript-player"
      onKeyDown={onKeyDown}
    >
      {recordings.length > 1 && (
        <div
          aria-label={t("visit.player.recordings")}
          className="mb-2 flex gap-1.5 overflow-x-auto"
          role="tablist"
        >
          {recordings.map((item, index) => (
            <button
              key={item.path}
              aria-selected={index === selected}
              className={cn(
                "h-8 shrink-0 rounded-full px-3 text-[12.5px] font-semibold transition-colors",
                index === selected
                  ? "bg-v3-ink text-v3-on-ink"
                  : "bg-v3-surface-2 text-v3-text-2 hover:bg-v3-surface-3",
              )}
              role="tab"
              type="button"
              onClick={() => onSelect(index)}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
      <div className="flex items-center gap-1.5">
        <button
          aria-label={t("visit.player.back", { seconds: SKIP_SECONDS })}
          className={iconButton}
          disabled={!current || !duration}
          title={t("visit.player.back", { seconds: SKIP_SECONDS })}
          type="button"
          onClick={() => useAudioEngine.getState().skip(-SKIP_SECONDS)}
        >
          <RotateCcw aria-hidden className="size-4" />
        </button>
        <button
          aria-label={
            playing ? t("visit.player.pause") : t("visit.player.play")
          }
          className="inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-v3-ink text-v3-on-ink transition-transform active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v3-brand-ring"
          data-testid="transcript-player-toggle"
          type="button"
          onClick={() => void toggle()}
        >
          {loading ? (
            <Loader2 aria-hidden className="size-4 animate-spin" />
          ) : playing ? (
            <Pause aria-hidden className="size-4" fill="currentColor" />
          ) : (
            <Play
              aria-hidden
              className="size-4 translate-x-px"
              fill="currentColor"
            />
          )}
        </button>
        <button
          aria-label={t("visit.player.forward", { seconds: SKIP_SECONDS })}
          className={iconButton}
          disabled={!current || !duration}
          title={t("visit.player.forward", { seconds: SKIP_SECONDS })}
          type="button"
          onClick={() => useAudioEngine.getState().skip(SKIP_SECONDS)}
        >
          <RotateCw aria-hidden className="size-4" />
        </button>
        <div className="flex min-w-0 flex-1 flex-col">
          <div
            ref={bar}
            aria-label={t("visit.player.seek")}
            aria-valuemax={Math.round(duration)}
            aria-valuemin={0}
            aria-valuenow={Math.round(time)}
            aria-valuetext={formatMoment(time * 1000)}
            className="relative flex h-7 min-w-0 cursor-pointer items-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v3-brand-ring"
            data-testid="transcript-player-seek"
            role="slider"
            tabIndex={0}
            onPointerDown={(event: PointerEvent<HTMLDivElement>) => {
              event.currentTarget.setPointerCapture?.(event.pointerId);
              seekAt(event.clientX);
            }}
            onPointerMove={(event: PointerEvent<HTMLDivElement>) => {
              if (event.buttons) seekAt(event.clientX);
            }}
          >
            <Waveform peaks={peaks} progress={progress} />
          </div>
          <div className="flex justify-between font-mono text-[11px] tabular-nums text-v3-text-3">
            <span>{formatMoment(time * 1000)}</span>
            <span>−{formatMoment(Math.max(0, duration - time) * 1000)}</span>
          </div>
        </div>
        <button
          aria-label={t("visit.player.speed", { rate: engine.rate })}
          className="inline-flex h-8 min-w-[44px] shrink-0 items-center justify-center rounded-full bg-v3-surface-2 px-2 font-mono text-[12px] font-semibold tabular-nums text-v3-text-2 shadow-v3-hairline transition-colors hover:bg-v3-surface-3"
          data-testid="transcript-player-speed"
          type="button"
          onClick={() => useAudioEngine.getState().cycleRate()}
        >
          {engine.rate}×
        </button>
      </div>
      {current && engine.status === "missing" && (
        <p
          className="mt-1.5 text-[12.5px] text-v3-text-3"
          data-testid="transcript-player-no-audio"
          role="status"
        >
          {t("visit.audioPlayer.noAudio")}
        </p>
      )}
      {current && engine.status === "error" && (
        <p className="mt-1.5 text-[12.5px] text-v3-rec-text" role="alert">
          {t("visit.audioPlayer.loadFailed")}
        </p>
      )}
    </div>
  );
};
